import { DashboardEditorProps } from "components/ComponentTypeTypes";
import { Divider } from "primereact/divider";
import { Dropdown, DropdownChangeEvent } from "primereact/dropdown";
import { InputSwitch } from "primereact/inputswitch";
import { InputText } from "primereact/inputtext";
import React, { FC } from "react";
import { T } from "services/lang/LanguageService";
import { useCompanyTimezone } from "hooks/useTimezone";
import {
  allPositionOptions,
  CurrentDateDisplayProps,
  MonthDisplayType,
  monthDisplayTypeOptions,
  TimeType,
  twoPositionOptions,
} from "../CurentDateDisplayTypes";
import getCurrentDate from "../CurrentDateDisplayUtils";

const CurrentDateDisplayEditor: FC<DashboardEditorProps<CurrentDateDisplayProps>> = ({
  config,
  onConfigChange,
}) => {
  const companyTz = useCompanyTimezone();

  const numberOfShownItems = [
    config.year?.show !== false,
    config.month?.show !== false,
    config.day?.show !== false,
  ].filter(Boolean).length;

  const positionOptions = numberOfShownItems === 2 ? twoPositionOptions : allPositionOptions;

  const handleShowSwitchChange = (timeType: TimeType, value: boolean) => {
    const updated = { ...config };
    updated[timeType] = { ...updated[timeType], show: value };
    if (!value) {
      updated[timeType].position = undefined;
      if (timeType === TimeType.MONTH) {
        updated[timeType].displayType = undefined;
      }
    }
    onConfigChange(updated);
  };

  const handlePositionChange = (timeType: TimeType) => (e: DropdownChangeEvent) => {
    const newPosition = e.value;
    const updated = { ...config };

    (Object.keys(config) as TimeType[]).forEach(type => {
      if (type !== timeType && config[type].position === newPosition) {
        updated[type] = { ...updated[type], position: undefined };
      }
    });

    updated[timeType] = { ...updated[timeType], position: newPosition };
    onConfigChange(updated);
  };

  const handleConfigChange = (timeType: TimeType, e: any) => {
    const updated = { ...config };
    updated[timeType] = { ...updated[timeType], [e.target.id]: e.target.value };
    onConfigChange(updated);
  };

  return (
    <div className="flex flex-column">
      <div className="flex flex-row justify-content-between">
        <div className="flex flex-row gap-3">
          <label>{T("label.current-date-example")}</label>
          <div>
            {getCurrentDate(
              companyTz,
              config.displayWeekOfYear,
              config.showFinishedWeek,
              config.year,
              config.month,
              config.day
            )}
          </div>
        </div>
        <div className="flex flex-column gap-2">
          <label htmlFor="displayWeekOfYear">{T("label.display-week-of-year")} </label>
          <InputSwitch
            checked={config.displayWeekOfYear ?? false}
            onChange={e => onConfigChange({ ...config, displayWeekOfYear: e.value })}
          />
        </div>
      </div>

      <Divider />

      {!config.displayWeekOfYear && (
        <>
          <div className="flex flex-row gap-3">
            <div className="flex flex-column gap-2 w-2">
              <label>{T("action.show-years")}</label>
              <InputSwitch
                checked={config.year?.show ?? true}
                onChange={e => handleShowSwitchChange(TimeType.YEAR, e.value)}
              />
            </div>
            {config.year?.show !== false && (
              <>
                {numberOfShownItems > 1 && (
                  <div className="flex flex-column gap-2 w-2">
                    <label>{T("label.year-position")}</label>
                    <Dropdown
                      value={config.year?.position}
                      options={positionOptions}
                      onChange={handlePositionChange(TimeType.YEAR)}
                    />
                  </div>
                )}
                <div className="flex flex-column gap-2 w-2">
                  <label htmlFor="suffix">{T("label.year-suffix")}</label>
                  <InputText
                    id="suffix"
                    value={config.year?.suffix}
                    onChange={e => handleConfigChange(TimeType.YEAR, e)}
                  />
                </div>
              </>
            )}
          </div>
          <Divider />
          <div className="flex flex-row gap-3">
            <div className="flex flex-column gap-2 w-2">
              <label>{T("action.show-months")}</label>
              <InputSwitch
                checked={config.month?.show ?? true}
                onChange={e => handleShowSwitchChange(TimeType.MONTH, e.value)}
              />
            </div>
            {config.month?.show !== false && (
              <>
                {numberOfShownItems > 1 && (
                  <div className="flex flex-column gap-2 w-2">
                    <label>{T("label.month-position")}</label>
                    <Dropdown
                      value={config.month?.position}
                      options={positionOptions}
                      onChange={handlePositionChange(TimeType.MONTH)}
                    />
                  </div>
                )}
                <div className="flex flex-column gap-2 w-2">
                  <label>{T("label.month-display-type")}</label>
                  <Dropdown
                    id="displayType"
                    value={config.month?.displayType ?? MonthDisplayType.NUMBER}
                    options={monthDisplayTypeOptions}
                    onChange={e => handleConfigChange(TimeType.MONTH, e)}
                  />
                </div>
                <div className="flex flex-column gap-2 w-2">
                  <label htmlFor="showFinishedMonth">{T("action.show-finished-month")}</label>
                  <InputSwitch
                    id="showFinishedMonth"
                    checked={config.month?.showFinishedMonth ?? false}
                    onChange={e => handleConfigChange(TimeType.MONTH, e)}
                  />
                </div>
              </>
            )}
          </div>
          <Divider />

          <div className="flex flex-row gap-3">
            <div className="flex flex-column gap-2 w-2">
              <label>{T("action.show-days")}</label>
              <InputSwitch
                checked={config.day?.show ?? true}
                onChange={e => handleShowSwitchChange(TimeType.DAY, e.value)}
              />
            </div>
            {config.day?.show !== false && numberOfShownItems > 1 && (
              <div className="flex flex-column gap-2 w-2">
                <label>{T("label.day-position")}</label>
                <Dropdown
                  value={config.day?.position}
                  options={positionOptions}
                  onChange={handlePositionChange(TimeType.DAY)}
                />
              </div>
            )}
          </div>
          <Divider />
        </>
      )}
      {config.displayWeekOfYear && (
        <div className="flex flex-column gap-2">
          <label htmlFor="showFinishedWeek">{T("action.show-finished-week")}</label>
          <InputSwitch
            id="showFinishedWeek"
            checked={config.showFinishedWeek ?? false}
            onChange={e => onConfigChange({ ...config, showFinishedWeek: e.value })}
          />
        </div>
      )}
    </div>
  );
};

export default CurrentDateDisplayEditor;
